import React, { useMemo, useEffect } from "react";

const CHILD_PARAMETERS = [
  "width",
  "height",
  "depth",
  "thickness",
  "boardThickness",
  "backThickness",
  "doorGap",
  "frontGap",
  "offsetX",
  "offsetY",
  "offsetZ",
];

const SOURCE_GROUPS = [
  {
    label: "Parent",
    options: [
      { label: "Parent width", value: "parent_width" },
      { label: "Parent height", value: "parent_height" },
      { label: "Parent depth", value: "parent_depth" },
      { label: "Parent board thickness", value: "parent_boardThickness" },
      { label: "Parent back thickness", value: "parent_backThickness" },
    ],
  },
  {
    label: "Project",
    options: [
      { label: "Project board thickness", value: "project_boardThickness" },
      { label: "Project back thickness", value: "project_backThickness" },
      { label: "Project default cabinet depth", value: "project_defaultCabinetDepth" },
    ],
  },
  {
    label: "Current / local",
    options: [
      { label: "Current width", value: "width" },
      { label: "Current height", value: "height" },
      { label: "Current depth", value: "depth" },
      { label: "Current board thickness", value: "boardThickness" },
    ],
  },
  {
    label: "Formula presets",
    options: [
      { label: "Parent width - board thickness × 2", value: "parent_width - parent_boardThickness * 2" },
      { label: "Parent height - board thickness × 2", value: "parent_height - parent_boardThickness * 2" },
      { label: "Parent depth - back thickness", value: "parent_depth - parent_backThickness" },
      { label: "Parent depth - 20", value: "parent_depth - 20" },
    ],
  },
];

function toRows(bindingsObj = {}) {
  return Object.entries(bindingsObj).map(([key, source], index) => ({
    id: `${key}_${index}`,
    key,
    source: source ?? "",
  }));
}

function toObject(rows = []) {
  const result = {};

  rows.forEach((row) => {
    const key = String(row.key || "").trim();
    if (!key) return;
    result[key] = row.source ?? "";
  });

  return result;
}

function createRow(key = "", source = "") {
  return {
    id: `bind_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    key,
    source,
  };
}

function BindingRow({ row, onChange, onRemove }) {
  return (
    <div className="border rounded p-3 bg-white space-y-2">
      <div className="grid grid-cols-5 gap-2 items-end">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Child parameter
          </label>
          <select
            value={row.key}
            onChange={(e) => onChange(row.id, "key", e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm bg-white"
          >
            <option value="">Select...</option>
            {CHILD_PARAMETERS.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Connect to
          </label>
          <select
            value={row.source}
            onChange={(e) => onChange(row.id, "source", e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm bg-white"
          >
            <option value="">Select source...</option>
            {SOURCE_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-[11px] text-gray-400">
          {row.key && row.source ? (
            <>
              {row.key} ← <code>{row.source}</code>
            </>
          ) : (
            "Choose a child parameter and source."
          )}
        </div>

        <button
          type="button"
          onClick={() => onRemove(row.id)}
          className="px-2 py-1 rounded border text-red-600 text-xs"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default function InstanceParameterBindingsInspector({ node, onChange }) {
  const rows = useMemo(
    () => toRows(node?.parameterBindings || {}),
    [node?.parameterBindings]
  );

  if (!node) {
    return (
      <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
        Select a child node to connect its parameters.
      </div>
    );
  }

  useEffect(() => {
  const face = node?.placement?.face;
  const mode = node?.placement?.mode;

  if (mode !== "snap") return;

  const preset = getPresetForFace(face);
  if (!preset) return;

  applyRows(toRows(preset));
}, [node?.placement?.face]);

  function applyRows(nextRows) {
    onChange?.({
      ...node,
      parameterBindings: toObject(nextRows),
    });
  }

  function handleAdd() {
    applyRows([...rows, createRow()]);
  }

  function handleRemove(rowId) {
    applyRows(rows.filter((row) => row.id !== rowId));
  }

  function handleChange(rowId, field, value) {
    applyRows(
      rows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  }

  function applyPreset(preset) {
    applyRows(toRows({ ...toObject(rows), ...preset }));
  }

  function getPresetForFace(face) {
  switch (face) {
    case "left":
    case "right":
      return {
        width: "parent_boardThickness",
        height: "parent_height",
        depth: "parent_depth",
      };

    case "top":
    case "bottom":
      return {
        width: "parent_width",
        height: "parent_boardThickness",
        depth: "parent_depth",
      };

    case "back":
      return {
        width: "parent_width",
        height: "parent_height",
        depth: "project_backThickness",
      };

    default:
      return null;
  }
}

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Parameter Connections</h3>
          <div className="text-xs text-gray-500">
            Connect child parameters to parent/project values without writing expressions manually.
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
        >
          + Add
        </button>
      </div>

      <div className="border rounded p-3 bg-gray-50 space-y-2">
        <div className="text-sm font-semibold">Cabinet presets</div>

        <div className="grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={() =>
              applyPreset({
                width: "parent_boardThickness",
                height: "parent_height",
                depth: "parent_depth",
              })
            }
            className="px-3 py-2 rounded border bg-white text-sm text-left"
          >
            Side board
          </button>

          <button
            type="button"
            onClick={() =>
              applyPreset({
                width: "parent_width",
                height: "parent_boardThickness",
                depth: "parent_depth",
              })
            }
            className="px-3 py-2 rounded border bg-white text-sm text-left"
          >
            Top / bottom / shelf board
          </button>

          <button
            type="button"
            onClick={() =>
              applyPreset({
                width: "parent_width",
                height: "parent_height",
                depth: "project_backThickness",
              })
            }
            className="px-3 py-2 rounded border bg-white text-sm text-left"
          >
            Back board
          </button>

          <button
            type="button"
            onClick={() =>
              applyPreset({
                width: "parent_width - parent_boardThickness * 2",
                height: "parent_height",
                depth: "parent_depth",
              })
            }
            className="px-3 py-2 rounded border bg-white text-sm text-left"
          >
            Inner element between side boards
          </button>
        </div>
      </div>

      {!rows.length ? (
        <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
          No connections yet. Add one or use a preset.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <BindingRow
              key={row.id}
              row={row}
              onChange={handleChange}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
}